import React, { createContext, useContext, useRef, useState, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getGlobalSounds } from '@/hooks/useSounds';

interface FlyingItem {
  id: string;
  imageUrl: string;
  startX: number;
  startY: number;
}

interface CartAnimationContextType {
  cartIconRef: React.RefObject<HTMLButtonElement>;
  triggerFlyAnimation: (imageUrl: string, startX: number, startY: number) => void;
  isPulsing: boolean;
}

const CartAnimationContext = createContext<CartAnimationContextType | undefined>(undefined);

export const CartAnimationProvider = ({ children }: { children: ReactNode }) => {
  const cartIconRef = useRef<HTMLButtonElement>(null);
  const [flyingItems, setFlyingItems] = useState<FlyingItem[]>([]);
  const [isPulsing, setIsPulsing] = useState(false);

  const triggerFlyAnimation = useCallback((imageUrl: string, startX: number, startY: number) => {
    const id = `fly-${Date.now()}`;
    setFlyingItems(prev => [...prev, { id, imageUrl, startX, startY }]);
    
    // Play add to cart sound
    const sounds = getGlobalSounds();
    sounds.playAddToCart();
    
    // Trigger pulse animation on badge
    setIsPulsing(true);
    setTimeout(() => setIsPulsing(false), 600);
  }, []);

  const handleFlyComplete = useCallback((id: string) => {
    setFlyingItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const getCartPosition = () => {
    if (cartIconRef.current) {
      const rect = cartIconRef.current.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }
    return { x: window.innerWidth - 50, y: 30 };
  };

  return (
    <CartAnimationContext.Provider value={{ cartIconRef, triggerFlyAnimation, isPulsing }}>
      {children}
      
      {/* Flying items portal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {flyingItems.map(item => {
            const endPos = getCartPosition();
            return (
              <motion.div
                key={item.id}
                className="fixed pointer-events-none z-[9999]"
                initial={{
                  x: item.startX,
                  y: item.startY,
                  scale: 1,
                  opacity: 1,
                }}
                animate={{
                  x: endPos.x - 25,
                  y: endPos.y - 25,
                  scale: 0.2,
                  opacity: 0,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.22, 0.61, 0.36, 1],
                }}
                onAnimationComplete={() => handleFlyComplete(item.id)}
              >
                <div 
                  className="w-16 h-16 rounded-xl overflow-hidden"
                  style={{
                    boxShadow: '0 0 30px hsl(0 75% 45% / 0.6)',
                  }}
                >
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>,
        document.body
      )}
    </CartAnimationContext.Provider>
  );
};

export const useCartAnimation = () => {
  const context = useContext(CartAnimationContext);
  if (!context) {
    throw new Error('useCartAnimation must be used within CartAnimationProvider');
  }
  return context;
};
